import { Flex } from '@chakra-ui/react';
import classNames from 'classnames';
import { startTransition, useLayoutEffect, useRef } from 'react';

import { TourClasses } from '@revelio/core';

import { usePageState } from '../PageState/PageStateProvider';
import { resolveActiveTab } from '../PageState/resolveActiveTab';
import styles from './TabManager.module.css';
import { useGetTabs, useUpdateTab } from './useTabs';

let lastSliderLeft: number | null = null;

export const NewLayoutTabManager = () => {
  const tabs = useGetTabs();
  const { pageState } = usePageState();
  const updateTab = useUpdateTab();

  const resolvedTab = resolveActiveTab(tabs, pageState.activeTabId);
  const activeIndex = resolvedTab
    ? tabs.findIndex((tab) => tab.id === resolvedTab.id)
    : 0;

  const tablistRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const tablist = tablistRef.current;
    const slider = sliderRef.current;
    if (!tablist || !slider) return;

    const tabButtons = tablist.querySelectorAll<HTMLElement>('[role="tab"]');
    const activeTab = tabButtons[activeIndex >= 0 ? activeIndex : 0];
    if (!activeTab) return;

    const target = activeTab.offsetLeft;

    if (lastSliderLeft === null) {
      slider.style.transition = 'none';
      slider.style.transform = `translate3d(${target}px, 0, 0)`;
      void slider.offsetWidth;
      slider.style.transition = '';
      lastSliderLeft = target;
      return;
    }

    if (lastSliderLeft === target) return;

    const raf = requestAnimationFrame(() => {
      slider.style.transform = `translate3d(${target}px, 0, 0)`;
      lastSliderLeft = target;
    });
    return () => cancelAnimationFrame(raf);
  }, [activeIndex]);

  const handleTabChange = (tabId: string) => {
    startTransition(() => {
      updateTab(tabId);
    });
  };

  if (tabs.length === 1) return null;

  return (
    <Flex alignItems="center" className={TourClasses.TOUR_VIEW_CLASS}>
      <div role="tablist" ref={tablistRef} className={styles.container}>
        <div
          ref={sliderRef}
          aria-hidden
          className={styles.slider}
          style={{
            transform: `translate3d(${lastSliderLeft ?? 0}px, 0, 0)`,
          }}
        />
        {tabs.map((tab, i) => {
          const isActive = activeIndex === i;
          return (
            <button
              key={tab.id}
              role="tab"
              type="button"
              aria-selected={isActive}
              onClick={() => handleTabChange(tab.id)}
              className={classNames(styles.tab, isActive && styles.tabActive)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    </Flex>
  );
};
